import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { faqList } from '@/mocks/about';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [newsletterError, setNewsletterError] = useState('');

  const featuredVideos = videos.filter((v) => v.featured).slice(0, 6);
  const hotVideos = videos.slice(0, 6);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim()) {
      setNewsletterStatus('success');
      return;
    }
    formData.delete('phone_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9rg5gnnahb7382ovm40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string } } = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setNewsletterStatus('success');
        form.reset();
      } else {
        setNewsletterStatus('error');
        setNewsletterError(parsed?.meta?.message || '订阅失败，请稍后重试');
      }
    } catch {
      setNewsletterStatus('error');
      setNewsletterError('网络错误，请检查连接后重试');
    }
  };

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '快猫短视频 - 海量短视频内容平台',
    description: '快猫短视频是一款汇聚海量优质短视频内容的娱乐平台，涵盖搞笑、美食、生活、才艺等多元类目。',
    url: 'https://www.kuaimao.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '快猫短视频',
      url: 'https://www.kuaimao.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20modern%20abstract%20digital%20landscape%20with%20warm%20gradient%20colors%20flowing%20like%20data%20streams%2C%20showing%20interconnected%20nodes%20and%20playful%20geometric%20shapes%2C%20representing%20a%20creative%20short%20video%20platform%2C%20warm%20coral%20and%20amber%20tones%2C%20dark%20background%20with%20bright%20highlights%2C%20digital%20art%20style%2C%20ethereal%20atmosphere&width=1800&height=900&seq=hero-kuaimao-main&orientation=landscape"
            alt="快猫短视频 - 发现精彩记录生活"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 md:mb-6">
              发现精彩，记录生活
            </h1>
            <p className="text-base md:text-lg text-background-50/85 max-w-2xl mb-8 md:mb-10 leading-relaxed">
              快猫短视频汇聚海量优质短视频内容，涵盖搞笑、美食、生活、才艺等多元类目。
              智能推荐算法为你发现更多精彩，创作者轻松上传即刻变现。
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4">
              <a
                href="#"
                className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm md:text-base hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即下载APP
              </a>
              <Link
                to="/category/gaoxiao"
                className="px-8 py-3 rounded-full border border-background-50/40 text-background-50 font-medium text-sm md:text-base hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                浏览热门视频
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">为什么选择快猫短视频</h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">全新升级的智能推荐系统，让每一次滑动都有惊喜</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-brain-line', title: '智能推荐', desc: 'AI算法精准匹配你的兴趣偏好，越刷越懂你' },
                { icon: 'ri-vidicon-line', title: '海量内容', desc: '搞笑、美食、旅行、才艺，应有尽有的内容生态' },
                { icon: 'ri-money-dollar-circle-line', title: '创作变现', desc: '多元变现方式，让每一位创作者获得应有回报' },
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: 'AI+人工双重审核，守护清朗健康的社区环境' },
              ].map((feat, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center hover:border-primary-200 transition-colors">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 rounded-xl bg-primary-50 flex items-center justify-center">
                    <i className={`${feat.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feat.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">探索内容分类</h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">八大内容类目，总有一个让你停不下来</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl p-5 text-center hover:border-accent-200 transition-all hover:-translate-y-1"
                >
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-50 flex items-center justify-center group-hover:bg-accent-100 transition-colors">
                    <i className={`${cat.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">{cat.name}</h3>
                  <p className="text-xs text-foreground-500">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">精选热门视频</h2>
                <p className="text-sm text-foreground-500">编辑团队为你精选的优质内容</p>
              </div>
              <Link to="/category/gaoxiao" className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
              {featuredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all"
                >
                  <div className="relative w-full aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      title={`${video.title} - 快猫短视频`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <span className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded-md">
                      {video.duration}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 leading-relaxed group-hover:text-primary-500 transition-colors">
                      {video.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {video.views >= 10000 ? `${(video.views / 10000).toFixed(0)}万` : video.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-heart-line"></i> {video.likes >= 10000 ? `${(video.likes / 10000).toFixed(0)}万` : video.likes}
                        </span>
                      </div>
                      <span className="bg-secondary-50 text-secondary-700 px-2 py-0.5 rounded-full text-xs">
                        {categories.find((c) => c.slug === video.category)?.name}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="sm:hidden mt-6 text-center">
              <Link to="/category/gaoxiao" className="inline-flex items-center gap-1 text-sm text-primary-500 font-medium whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="relative w-full py-14 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20digital%20creative%20studio%20environment%20with%20warm%20ambient%20lighting%20and%20floating%20geometric%20shapes%2C%20modern%20content%20creation%20concept%2C%20coral%20and%20amber%20gradient%20accents%2C%20minimalist%20aesthetic%2C%20inspirational%20creative%20workspace%20atmosphere&width=1800&height=600&seq=creator-cta-bg&orientation=landscape"
            alt="创作者入驻"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/90 to-accent-600/80"></div>
          <div className="relative z-10 w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">成为快猫创作者</h2>
            <p className="text-sm md:text-base text-background-50/85 mb-8 leading-relaxed">
              无论你是搞笑达人、美食博主还是才艺高手，快猫短视频为你提供展示才华的舞台。
              智能推荐精准触达目标观众，多元变现让创作更有价值。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <a href="#" className="px-8 py-3 rounded-full bg-background-50 text-primary-600 font-semibold text-sm md:text-base hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                立即入驻创作
              </a>
              <a href="#" className="px-8 py-3 rounded-full border border-background-50/50 text-background-50 font-medium text-sm md:text-base hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer">
                了解创作者计划
              </a>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {[
                { label: '日活用户', value: '5000万+' },
                { label: '入驻创作者', value: '200万+' },
                { label: '日均视频上传', value: '100万+' },
                { label: '覆盖国家地区', value: '30+' },
              ].map((stat, i) => (
                <div key={i} className="text-center p-5">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">常见问题</h2>
              <p className="text-sm text-foreground-500">快速了解快猫短视频</p>
            </div>
            <div className="flex flex-col gap-3">
              {faqList.map((faq, i) => (
                <div key={i} className="border border-background-200/70 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
                  >
                    <span>{faq.q}</span>
                    {openFaq === i ? (
                  <i className="ri-arrow-up-s-line text-foreground-400"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-foreground-400"></i>
                )}
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed border-t border-background-200/70 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-foreground-950">
          <div className="w-full px-4 md:px-6 max-w-xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">订阅快猫资讯</h2>
            <p className="text-sm text-foreground-300 mb-8">第一时间获取热门内容推荐和平台最新动态</p>
            <form onSubmit={handleNewsletterSubmit} data-readdy-form="" className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                name="email"
                required
                placeholder="请输入您的邮箱"
                className="flex-1 px-4 py-3 rounded-full bg-foreground-800 border border-foreground-700 text-sm text-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="antispam-field" />
              <button
                type="submit"
                className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即订阅
              </button>
            </form>
            {newsletterStatus === 'success' && (
              <p className="mt-4 text-sm text-green-400">订阅成功！感谢你的关注。</p>
            )}
            {newsletterStatus === 'error' && (
              <p className="mt-4 text-sm text-red-400">{newsletterError || '订阅失败，请稍后重试'}</p>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}